import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Box, Button, Collapse, Paper, Stack, TextField, Typography } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import LereService from '~/services/LereService';

import { hasFullAccess, hasReadAccess } from '~/utils/lere';

import { LereTabComponentProps } from './types';

type FormInput = {
  identifier: string;
};

type ClearedIds = {
  userID?: string;
  domain_user_ids?: Array<string>;
};

export default function LereUserActivity({ onLereError }: LereTabComponentProps) {
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const [activityData, setActivityData] = useState(null);
  const [clearedResult, setClearedResult] = useState(null);
  const [isGetting, setIsGetting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    getValues,
  } = useForm<FormInput>({
    defaultValues: {
      identifier: '',
    },
  });

  const onClearSubmit: SubmitHandler<FormInput> = async (data) => {
    try {
      const value = data.identifier?.trim();
      if (!value) {
        enqueueSnackbar('Enter a User ID or a Domain User ID', { variant: 'warning' });
        return;
      }

      const result = await LereService.clearUserInteractions({ userId: value, domainUserId: value });
      enqueueSnackbar('Activity cleared successfully', { variant: 'success' });
      setActivityData(null); // Clear any displayed activity data
      setClearedResult(result); // Show results of /clear
    } catch (e) {
      onLereError(e);
    }
  };

  const onGetActivity = async () => {
    try {
      const value = getValues('identifier')?.trim();
      if (!value) {
        enqueueSnackbar('Enter a User ID or a Domain User ID', { variant: 'warning' });
        return;
      }

      setIsGetting(true);
      const response = await LereService.getUserInteractions({ userId: value, domainUserId: value });
      setActivityData(response);
      setClearedResult(null); // Hide previous clear summary when fetching activity
      enqueueSnackbar('Activity data retrieved successfully', { variant: 'success' });
    } catch (e) {
      onLereError(e);
      setActivityData(null);
    } finally {
      setIsGetting(false);
    }
  };

  // Helper functions for formatting clear result data
  const formatClearedIds = useCallback((clearedIds: ClearedIds) => {
    const userID = clearedIds?.userID || '-';
    const domainUserIds = Array.isArray(clearedIds?.domain_user_ids) ? clearedIds.domain_user_ids.join(', ') : '-';
    return `User ID: ${userID}, Domain User IDs: ${domainUserIds}`;
  }, []);

  const formatInteractionTypes = useCallback((interactionTypes: Array<string> | unknown) => {
    return Array.isArray(interactionTypes) ? interactionTypes.join(', ') : '-';
  }, []);

  const formatTimestamp = useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  }, []);

  // Computed values for clear result display
  const clearResultData = clearedResult
    ? {
        message: clearedResult.result.message,
        clearedKeys: clearedResult.result.cleared_keys,
        clearedIds: formatClearedIds(clearedResult.result.cleared_ids),
        interactionTypes: formatInteractionTypes(clearedResult.result.interaction_types),
        eventsRemovedTotal: clearedResult.result.events_removed_total,
        timestamp: clearedResult.meta?.timestamp ? formatTimestamp(clearedResult.meta.timestamp) : null,
        eventsRemovedByInteraction: clearedResult.result.events_removed_by_interaction,
      }
    : null;

  const hasAccess = hasReadAccess(user) || hasFullAccess(user);

  if (!hasAccess) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6">User Activity Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Enter either a User ID or a Domain User ID to get or clear user activity data.
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{ flexGrow: 1, maxWidth: 400 }}>
            <Controller
              name="identifier"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="User ID or Domain User ID"
                  placeholder="Enter user ID or domain user ID"
                  fullWidth
                  disabled={isSubmitting || isGetting}
                />
              )}
            />
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              disabled={isSubmitting || isGetting}
              onClick={onGetActivity}
              size="large"
              color="secondary"
              variant="outlined"
              sx={{ minWidth: 120, height: 40 }}
            >
              {isGetting ? 'Getting...' : 'Get Activity'}
            </Button>

            <Button
              disabled={isSubmitting || isGetting}
              onClick={handleSubmit(onClearSubmit)}
              size="large"
              color="primary"
              variant="contained"
              sx={{ minWidth: 140, height: 40 }}
            >
              {isSubmitting ? 'Clearing...' : 'Clear Activity'}
            </Button>
          </Stack>
        </Stack>

        <Collapse in={!!activityData}>
          {activityData && (
            <Paper sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Activity Data
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 400,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                }}
              >
                {JSON.stringify(activityData, null, 2)}
              </Box>
            </Paper>
          )}
        </Collapse>

        <Collapse in={!!clearResultData}>
          {clearResultData && (
            <Paper sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Clear Result
              </Typography>
              <Stack spacing={0.5} sx={{ mb: 1 }}>
                <Typography variant="body2">{clearResultData.message}</Typography>
                <Typography variant="body2">Cleared keys: {clearResultData.clearedKeys}</Typography>
                <Typography variant="body2">Cleared IDs: {clearResultData.clearedIds}</Typography>
                <Typography variant="body2">Interaction types: {clearResultData.interactionTypes}</Typography>
                <Typography variant="body2">Events removed (total): {clearResultData.eventsRemovedTotal}</Typography>
                {clearResultData.timestamp && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    Cleared at: {clearResultData.timestamp}
                  </Typography>
                )}
              </Stack>
              <Typography variant="subtitle2" gutterBottom>
                Events removed by interaction
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 240,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                }}
              >
                {JSON.stringify(clearResultData.eventsRemovedByInteraction, null, 2)}
              </Box>
            </Paper>
          )}
        </Collapse>
      </Stack>
    </Box>
  );
}
